import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Stamp, QrCode, MessageCircle, Gift, BarChart3, Shield, 
  Check, ArrowRight, Star, Zap, Users, TrendingUp, MapPin, ChevronRight, Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [carouselLogos, setCarouselLogos] = useState([]);
  const location = useLocation();

  useEffect(() => {
    // Scroll to top when navigating to home without hash
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
    // Fetch carousel logos
    fetchCarouselLogos();
  }, [location]);

  const fetchCarouselLogos = async () => {
    try {
      const response = await axios.get(`${API}/public/carousel-logos`);
      setCarouselLogos(response.data.logos || []);
    } catch (error) {
      console.error('Error fetching carousel logos:', error);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const features = [
    {
      icon: <QrCode className="w-7 h-7" />,
      title: "QR en el punto de venta",
      description: "El cliente escanea, envía un WhatsApp y suma su visita. Sin apps, sin registro.",
      color: "bg-blue-500"
    },
    {
      icon: <MessageCircle className="w-7 h-7" />,
      title: "WhatsApp como canal único",
      description: "Todo por el canal que ya usan tus clientes. Cero fricción, máxima adopción.",
      color: "bg-green-500"
    },
    {
      icon: <Gift className="w-7 h-7" />,
      title: "Premios automáticos",
      description: "Vouchers con código único que se generan automáticamente al completar sellos.",
      color: "bg-purple-500"
    },
    {
      icon: <Zap className="w-7 h-7" />,
      title: "Promociones doble sello",
      description: "Configura días y horarios especiales para incentivar visitas en horas valle.",
      color: "bg-amber-500"
    },
    {
      icon: <MapPin className="w-7 h-7" />,
      title: "Validación por ubicación",
      description: "Geofencing + PIN de respaldo para verificar que el cliente está en tu local.",
      color: "bg-rose-500"
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Sistema antifraude",
      description: "Tokens temporales, rate limiting y validación geográfica evitan abusos.",
      color: "bg-teal-500"
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "0€",
      period: "/mes",
      description: "Perfecto para probar",
      features: ["1 campaña activa", "1 local", "100 sellos/mes", "QR + Cartelería", "Soporte Básico"],
      cta: "Empezar Gratis",
      popular: false
    },
    {
      name: "Growth",
      price: "29€",
      period: "/mes",
      description: "Para negocios en crecimiento",
      features: ["5 campañas activas", "1 local", "5.000 sellos/mes", "QR + Cartelería", "Doble sello", "Exportación CSV", "Soporte Chat 24h"],
      cta: "Elegir Growth",
      popular: true
    },
    {
      name: "Scale",
      price: "95€",
      period: "/mes",
      description: "Para pequeñas cadenas",
      features: ["15 campañas activas", "5 locales", "25.000 sellos/mes", "QR + Cartelería", "Doble sello", "Multi-local", "Exportación CSV", "Soporte Chat 24h"],
      cta: "Elegir Scale",
      popular: false
    },
    {
      name: "Elite",
      price: "395€",
      period: "/mes",
      description: "Para cadenas y franquicias",
      features: ["Campañas ilimitadas", "Hasta 50 locales", "Sellos ilimitados", "QR + Cartelería", "Doble sello", "Multi-local", "Exportación CSV", "Soporte Chat 24h"],
      cta: "Elegir Elite",
      popular: false
    }
  ];

  const stats = [
    { value: "+30%", label: "Más visitas recurrentes" },
    { value: "88%", label: "Tasa de retención" },
    { value: "5min", label: "Setup inicial" },
    { value: "0", label: "Apps que descargar" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28 sm:h-32 lg:h-36">
            <Link to="/" onClick={scrollToTop} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/n7zu75fp_file_00000000fb6c7243ac605c38dbc5a990%20%281%29.png" 
                alt="Stampply Rewards" 
                className="h-auto w-40 sm:w-48 lg:w-56"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Características</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Precios</a>
            </div>
            
            <div className="hidden sm:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-slate-700 hover:text-slate-900">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary">
                  Prueba Gratis
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="sm:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menú"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div 
              className="sm:hidden border-t border-slate-100 py-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex flex-col gap-3">
                <a 
                  href="#features" 
                  className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Características
                </a>
                <a 
                  href="#pricing" 
                  className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Precios
                </a>
                <div className="border-t border-slate-100 pt-3 mt-2 flex flex-col gap-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-center">
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="btn-primary w-full justify-center">
                      Prueba Gratis
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 overflow-hidden bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Text Content - Always first on mobile */}
            <motion.div 
              className="text-center lg:text-left"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-sm font-medium mb-6 border border-white/10">
                  <Star className="w-4 h-4 text-amber-400" fill="currentColor" /> 
                  Fidelización sin fricción
                </span>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-[1.1] mb-6"
              >
                Fideliza tus clientes con{' '}
                <span className="text-[#25D366]">WhatsApp</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-base sm:text-lg lg:text-xl text-white/75 mb-8 max-w-lg mx-auto lg:mx-0"
              >
                Sistema de sellos digitales por WhatsApp. Tu cliente escanea, envía un mensaje y acumula visitas. Sin apps, sin fricciones.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/register">
                  <Button className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 w-full sm:w-auto">
                    Empezar Gratis <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/como-funciona">
                  <Button variant="outline" className="bg-white/5 border-2 border-white/20 text-white hover:bg-white/10 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 w-full sm:w-auto">
                    Ver cómo funciona
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div 
                variants={fadeInUp}
                className="hidden sm:flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 mt-8 text-white/60 text-sm"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>Setup en 5 minutos</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>Fácilmente escalable</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>Sin tarjeta física</span>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Image - Second on mobile */}
            <motion.div 
              className="relative mt-8 lg:mt-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Glow effect */}
                <div className="absolute -inset-4 bg-secondary/20 rounded-3xl blur-2xl opacity-50" />
                
                {/* Image */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/pqn1ahgq_portada%20cuadrada%20de%20Stampply.png" 
                  alt="Stampply Rewards"
                  className="relative w-full h-auto rounded-2xl shadow-2xl shadow-black/30"
                />
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Wave Divider */}
        <div className="relative -mb-1">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L48 72C96 64 192 48 288 40C384 32 480 32 576 36C672 40 768 48 864 52C960 56 1056 56 1152 52C1248 48 1344 40 1392 36L1440 32V80H1392C1344 80 1248 80 1152 80C1056 80 960 80 864 80C768 80 672 80 576 80C480 80 384 80 288 80C192 80 96 80 48 80H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Social Proof Bar - Dynamic Carousel */}
      {carouselLogos.length > 0 && (
        <section className="py-10 bg-white border-b border-slate-100 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm font-medium uppercase tracking-wider text-slate-400 mb-6">
              Confían en nosotros
            </p>
            <div className="relative">
              <motion.div 
                className="flex items-center gap-12"
                animate={{ x: [0, -50 * carouselLogos.length] }}
                transition={{ 
                  duration: carouselLogos.length * 3, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
              >
                {/* Duplicate logos for seamless loop */}
                {[...carouselLogos, ...carouselLogos, ...carouselLogos].map((logo, index) => (
                  <div 
                    key={index} 
                    className="flex-shrink-0 flex items-center justify-center px-4"
                    title={logo.name}
                  >
                    <img 
                      src={logo.logo_url.startsWith('http') ? logo.logo_url : `${process.env.REACT_APP_BACKEND_URL}${logo.logo_url}`}
                      alt={logo.name}
                      className="h-10 sm:h-12 max-w-[120px] object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* How it Works */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Así de simple funciona
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tres pasos. Sin apps. Sin complicaciones.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { 
                step: "01", 
                title: "Escanea el QR", 
                desc: "El cliente escanea el código QR en tu punto de venta con su móvil", 
                icon: <QrCode className="w-8 h-8" />,
                color: "from-blue-500 to-blue-600"
              },
              { 
                step: "02", 
                title: "Envía por WhatsApp", 
                desc: "Se abre WhatsApp automáticamente con un mensaje que registra su visita", 
                icon: <MessageCircle className="w-8 h-8" />,
                color: "from-green-500 to-green-600"
              },
              { 
                step: "03", 
                title: "¡Recibe su premio!", 
                desc: "Al completar los sellos, recibe un voucher digital con su recompensa", 
                icon: <Gift className="w-8 h-8" />,
                color: "from-purple-500 to-purple-600"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 h-full">
                  <div className={`w-16 h-16 mb-6 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                    {item.icon}
                  </div>
                  <span className="text-6xl font-extrabold text-slate-100 absolute top-4 right-6">{item.step}</span>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Herramientas diseñadas para comercios que quieren más clientes recurrentes.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className={`w-14 h-14 mb-5 ${feature.color} rounded-xl flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-2">{stat.value}</div>
                <div className="text-white/70 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Planes simples, sin sorpresas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Empieza gratis y escala cuando lo necesites.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                className={`relative rounded-2xl p-6 ${
                  plan.popular 
                    ? 'bg-primary text-white ring-4 ring-primary/20 scale-105 z-10' 
                    : 'bg-white border-2 border-slate-200'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-block px-4 py-1 bg-secondary text-white text-sm font-semibold rounded-full shadow-lg">
                      Más popular
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.popular ? 'text-white/70' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                </div>
                
                <div className="flex items-baseline gap-1 mb-6">
                  <span className={`text-5xl font-extrabold ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.popular ? 'text-white/70' : 'text-slate-500'}>
                    {plan.period}
                  </span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-secondary' : 'text-secondary'}`} />
                      <span className={plan.popular ? 'text-white/90' : 'text-slate-600'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/register" className="block">
                  <Button 
                    className={`w-full py-4 font-semibold ${
                      plan.popular 
                        ? 'bg-white text-primary hover:bg-white/90' 
                        : 'btn-primary'
                    }`}
                  >
                    {plan.cta} <ChevronRight className="w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              ¿Listo para fidelizar clientes?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Únete a cientos de comercios que ya usan Stampply para aumentar sus visitas recurrentes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="btn-primary text-lg px-8 py-4">
                  Crear cuenta gratis <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/como-funciona">
                <Button variant="outline" className="bg-transparent border-2 border-slate-600 text-white hover:bg-slate-800 text-lg px-8 py-4">
                  Ver cómo funciona
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-10">
            {/* Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/tgu072lj_LOGO%20STAMPPLY%202.png" 
              alt="Stampply Rewards" 
              className="h-32 sm:h-40 md:h-48 w-auto"
            />
            
            {/* Links */}
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-slate-400">
              <Link to="/terminos" className="hover:text-white transition-colors font-medium">Términos</Link>
              <Link to="/privacidad" className="hover:text-white transition-colors font-medium">Privacidad</Link>
              <Link to="/contacto" className="hover:text-white transition-colors font-medium">Contacto</Link>
            </div>
            
            {/* Copyright */}
            <p className="text-slate-600 text-sm">
              © 2024 Stampply Rewards. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
